import React, { useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { useBrokerReport } from '../../hooks/useBrokerReport';
import { useLoanBookView } from '../../hooks/useLoanBookView';
import { brokerReportFetcher } from '../../hooks/loanBookViewFetchers';
import { useHideCommissions } from '../../hooks/useHideCommissions';
import { ArrowLeft, Loader2, AlertCircle, Share2, Copy, Check, RefreshCw } from 'lucide-react';
import { Pagination } from '../../components/common/Pagination';
import { LoanBookValuePanel } from '../../components/reports/LoanBookValuePanel';
import { HelpButton } from '../../components/help/HelpButton';
import { COMMISSION_GST_BASIS, formatCommissionValue } from '../../utils/commissionDisplay';
import { formatCompactCurrency } from '../../utils/formatters';

const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(dollars);
};

// Cards: the app-wide three-significant-figure rule. Tables keep formatCurrency.
const formatCurrencyCompact = (cents: number): string => formatCompactCurrency(cents);

const formatDate = (dateStr: string): string => {
  if (!dateStr) return 'N/A';
  const d = typeof dateStr === 'object' && (dateStr as any)?.value ? (dateStr as any).value : dateStr;
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatMonth = (monthStr: string): string => {
  const [year, month] = monthStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('en-AU', { month: 'short' });
};

interface OutletContextType {
  userData: any;
  companyData: any;
  userVisibleOffices: any[];
}

const BrokerReportPage: React.FC = () => {
  const { brokerDisplay: encodedBrokerDisplay } = useParams<{ brokerDisplay: string }>();
  const brokerDisplay = decodeURIComponent(encodedBrokerDisplay || '');
  const navigate = useNavigate();
  const { userData } = useOutletContext<OutletContextType>();
  const companyId = userData?.companyId || '';
  const isSuperAdmin = userData?.role === 'super_admin';

  const { data, loading, error, refetch } = useBrokerReport(companyId, brokerDisplay);
  const hideCommissions = useHideCommissions();

  // Scoped loan book view (Phases 3 + 6 of the 2026-08-04 LM reconciliation).
  //
  // The view arrives WITH the report, so the initial render costs no extra call;
  // only a scope change re-requests. `loanBookView` is undefined until the report
  // has loaded (hence `enabled`), and is null-or-absent whenever the tenant flag
  // is off, in which case the report keeps its existing single card rather than
  // inventing a split.
  const {
    view: loanBookView,
    setMode: setLoanBookMode,
    loading: loanBookLoading,
  } = useLoanBookView(
    { companyId, brokerDisplay },
    {
      fetcher: brokerReportFetcher,
      enabled: Boolean(data),
      initialView: data ? (data.loanBookView ?? null) : undefined,
    },
  );

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Pagination state for tables
  const [clawbackPage, setClawbackPage] = useState(1);
  const [clawbackPageSize, setClawbackPageSize] = useState(25);
  const [dischargedPage, setDischargedPage] = useState(1);
  const [dischargedPageSize, setDischargedPageSize] = useState(25);
  const [settlementsPage, setSettlementsPage] = useState(1);
  const [settlementsPageSize, setSettlementsPageSize] = useState(25);

  const handleGenerateShareLink = async () => {
    setShareLoading(true);
    try {
      const fn = httpsCallable(functions, 'generateBrokerReportShareToken');
      const result = await fn({ companyId, brokerDisplay, expiresInDays: 30 });
      const response = result.data as { shareUrl: string; expiresAt: string };
      setShareUrl(response.shareUrl);
      setShareModalOpen(true);
    } catch (err) {
      console.error('Failed to generate share link:', err);
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-400">Generating broker performance report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-white mb-2">Failed to Load Report</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { executiveSummary: exec, allTimeSummary, bookHealth, commissionTrend, lenderConcentration, templateBreakdown, clawbackEventsList, dischargedLoansList, newLoans } = data;

  // Commission trend chart calculations
  const maxTrendTotal = Math.max(...commissionTrend.map(m => m.upfrontCents + m.trailCents), 1);
  const totalLenderLoans = lenderConcentration.reduce((sum, l) => sum + l.loanCount, 0);
  const maxLenderCount = Math.max(...lenderConcentration.map(l => l.loanCount), 1);

  // Lender colors
  const lenderColors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#475569'];

  return (
    <div className="space-y-6 max-w-[1100px] mx-auto">
      {/* Top bar with back button and actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/reports/broker-loan-books')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Loan Books
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleGenerateShareLink}
              disabled={shareLoading}
              className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {shareLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
              Share Report
            </button>
          )}
        </div>
      </div>

      {/* Header with logo */}
      <div className="relative pb-5 border-b-2 border-emerald-500">
        <div className="flex justify-between items-start">
          <img src="/valutten-logo.jpg" alt="VALUTTEN" className="h-12" style={{ mixBlendMode: 'lighten' }} />
          <div className="text-right">
            <span className="inline-block bg-white/5 border border-white/10 rounded-md px-2.5 py-1 text-[11px] uppercase tracking-widest text-gray-400 mb-1">
              Confidential Report
            </span>
            <div className="text-sm text-gray-500">
              {new Date(data.reportDate).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-white mt-4">{data.brokerName} — Broker Performance Report</h1>
        <p className="text-sm text-gray-400 mt-1">Prepared by {data.companyName}</p>
      </div>

      {/* Executive Summary.
          When the scoped loan book view is available the Loan Book Value card
          moves into its own section below, so the number is never shown without
          its entity scope. Until then the legacy card stays, but it now states
          the scope it has always been measured in. */}
      <section>
        <SectionHeading title="Executive Summary" period={`Performance Period: ${exec.period}`} />
        <div className={`grid grid-cols-2 gap-3 ${loanBookView ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
          <MetricCard label="Upfront Commission" value={formatCommissionValue(exec.upfrontCents, hideCommissions, formatCurrency)} sub={`${exec.period} YTD • ${COMMISSION_GST_BASIS}`} color="#ec4899" />
          <MetricCard label="Trail Commission" value={formatCommissionValue(exec.trailCents, hideCommissions, formatCurrency)} sub={`${exec.period} YTD • ${COMMISSION_GST_BASIS}`} color="#3b82f6" />
          {!loanBookView && (
            <MetricCard label="Loan Book Value" value={formatCommissionValue(exec.loanBookCents, hideCommissions, formatCurrencyCompact)} sub="All loan books (every entity)" color="#f59e0b" hero />
          )}
          <MetricCard label="Line of Trail" value={String(exec.lineOfTrail)} sub="Active loans" color="#8b5cf6" />
        </div>
      </section>

      {/* Loan Book: entity scope, valuation composition and per-entity freshness. */}
      {loanBookView && (
        <LoanBookValuePanel
          view={loanBookView}
          onModeChange={setLoanBookMode}
          hideCommissions={hideCommissions}
          busy={loanBookLoading}
          helpSlot={<HelpButton pageKey="loanBookValue" variant="pill" />}
        />
      )}

      {/* All-Time Performance */}
      <section>
        <SectionHeading title="All-Time Performance" period={`Since ${allTimeSummary.earliestYear}`} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <MetricCard label="Total Loans Written" value={allTimeSummary.totalLoans.toLocaleString()} sub="Unique loan IDs" color="#06b6d4" />
          <MetricCard label="Total Commission Earned" value={formatCurrency(allTimeSummary.totalCommissionCents)} sub={`Upfront + Trail combined • ${COMMISSION_GST_BASIS}`} color="#10b981" />
          <MetricCard label="Average Loan Size" value={formatCurrency(allTimeSummary.averageLoanSizeCents)} sub="Per unique loan" color="#f59e0b" />
        </div>
      </section>

      {/* Book Health */}
      <section>
        <SectionHeading title="Book Health" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <HealthCard value={bookHealth.activeLoans} label="Active Loans" desc="Loans currently generating trail commission" color="#10b981" />
          <HealthCard value={bookHealth.dischargedLoans} label="Discharged" desc="Loans fully paid out or refinanced away" color="#6b7280" />
          <HealthCard value={bookHealth.clawbackEvents} label="Clawbacks" desc="Commission clawback events" color="#ef4444" />
          <HealthCard value={bookHealth.arrearsLoans} label="Arrears" desc="Loans currently in arrears" color="#f59e0b" />
        </div>
      </section>

      {/* Loan Books */}
      {templateBreakdown && templateBreakdown.length > 0 && (
        <section>
          <SectionHeading title="Loan Books" period={`${templateBreakdown.length} book${templateBreakdown.length !== 1 ? 's' : ''}`} />
          <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Book</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Office</th>
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Date Range</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Loans</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Upfront</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Trail</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Total</th>
                </tr>
              </thead>
              <tbody>
                {templateBreakdown.map((t, i) => (
                  <tr key={`${t.templateId}-${t.officeName}`} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                    <td className="px-4 py-2.5 text-white font-medium">{t.templateName}</td>
                    <td className="px-4 py-2.5 text-gray-400">{t.officeName}</td>
                    <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap text-xs">
                      {t.firstRunDate && t.lastRunDate
                        ? `${formatDate(t.firstRunDate)} — ${formatDate(t.lastRunDate)}`
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-2.5 text-right text-white">{t.loanCount.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-gray-300 font-mono">{formatCurrency(t.upfrontCents)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-300 font-mono">{formatCurrency(t.trailCents)}</td>
                    <td className="px-4 py-2.5 text-right text-white font-mono font-medium">{formatCurrency(t.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Commission Trend */}
      {commissionTrend.length > 0 && (
        <section>
          <SectionHeading title={`Commission Trend — Last ${commissionTrend.length} Months`} />
          <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-end gap-3 h-52">
              {commissionTrend.map((month) => {
                const total = month.upfrontCents + month.trailCents;
                const heightPct = (total / maxTrendTotal) * 100;
                const upfrontPct = total > 0 ? (month.upfrontCents / total) * 100 : 0;
                return (
                  <div key={month.month} className="flex-1 flex flex-col items-center h-full justify-end">
                    <div className="w-full max-w-[56px] flex flex-col justify-end rounded-t-md overflow-hidden" style={{ height: `${heightPct}%` }}>
                      <div style={{ height: `${upfrontPct}%`, background: '#ec4899' }} />
                      <div style={{ height: `${100 - upfrontPct}%`, background: '#3b82f6' }} />
                    </div>
                    <div className="text-[11px] font-mono text-gray-400 mt-2">{formatCurrencyCompact(total)}</div>
                    <div className="text-xs text-gray-500 mt-1">{formatMonth(month.month)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-5 mt-4 pt-4 border-t border-white/[0.06]">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#ec4899' }} /> Upfront
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#3b82f6' }} /> Trail
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Clawback Events */}
      {clawbackEventsList.length > 0 && (() => {
        const clawbackSlice = clawbackEventsList.slice((clawbackPage - 1) * clawbackPageSize, clawbackPage * clawbackPageSize);
        return (
          <section>
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="flex justify-between items-center px-5 py-3 border-b border-red-500/20">
                <h3 className="text-base font-semibold text-red-400">Clawback Events</h3>
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-red-500/10 text-red-400">
                  {clawbackEventsList.length} Events — {formatCurrency(bookHealth.clawbackTotalCents)} Total
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-red-500/[0.04]">
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Loan ID</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Customer</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Amount</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Settlement</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Clawback Date</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Lender</th>
                  </tr>
                </thead>
                <tbody>
                  {clawbackSlice.map((e, i) => (
                    <tr key={e.loanId} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                      <td className="px-4 py-2.5 font-mono text-xs text-emerald-400">{e.loanId}</td>
                      <td className="px-4 py-2.5 text-gray-400">{e.customerName}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-medium text-red-400">{formatCurrency(e.clawbackAmountCents)}</td>
                      <td className="px-4 py-2.5 text-gray-400">{formatDate(e.originalSettlementDate)}</td>
                      <td className="px-4 py-2.5 text-gray-400">{formatDate(e.clawbackDate)}</td>
                      <td className="px-4 py-2.5 text-gray-400">{e.lender}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {clawbackEventsList.length > clawbackPageSize && (
                <Pagination
                  currentPage={clawbackPage}
                  totalItems={clawbackEventsList.length}
                  pageSize={clawbackPageSize}
                  onPageChange={setClawbackPage}
                  onPageSizeChange={(size) => { setClawbackPageSize(size); setClawbackPage(1); }}
                />
              )}
            </div>
          </section>
        );
      })()}

      {/* Discharged Loans */}
      {dischargedLoansList.length > 0 && (() => {
        const dischargedSlice = dischargedLoansList.slice((dischargedPage - 1) * dischargedPageSize, dischargedPage * dischargedPageSize);
        const startIdx = (dischargedPage - 1) * dischargedPageSize;
        return (
          <section>
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="flex justify-between items-center px-5 py-3 border-b border-red-500/20">
                <h3 className="text-base font-semibold text-gray-300">Discharged Loans</h3>
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-white/5 text-gray-400">
                  {dischargedLoansList.length} Total Discharged
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-red-500/[0.04]">
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 w-[4%]">#</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Loan ID</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Customer</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Lender</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Discharge Date</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Loan Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {dischargedSlice.map((loan, i) => (
                    <tr key={loan.loanId} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                      <td className="px-4 py-2.5 text-gray-500">{startIdx + i + 1}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-emerald-400">{loan.loanId}</td>
                      <td className="px-4 py-2.5 text-gray-400">{loan.customerName}</td>
                      <td className="px-4 py-2.5 text-gray-400">{loan.lender}</td>
                      <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">{formatDate(loan.settlementDate)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-medium text-white">{formatCurrency(loan.loanAmountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {dischargedLoansList.length > dischargedPageSize && (
                <Pagination
                  currentPage={dischargedPage}
                  totalItems={dischargedLoansList.length}
                  pageSize={dischargedPageSize}
                  onPageChange={setDischargedPage}
                  onPageSizeChange={(size) => { setDischargedPageSize(size); setDischargedPage(1); }}
                />
              )}
            </div>
          </section>
        );
      })()}

      {/* Recent Settlements */}
      {newLoans.length > 0 && (() => {
        const settlementsSlice = newLoans.slice((settlementsPage - 1) * settlementsPageSize, settlementsPage * settlementsPageSize);
        const startIdx = (settlementsPage - 1) * settlementsPageSize;
        return (
          <section>
            <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="flex justify-between items-center px-5 py-3 border-b border-white/[0.06]">
                <h3 className="text-base font-semibold text-white">Recent Settlements</h3>
                <span className="text-xs font-medium px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400">
                  {data.portfolioSummary.totalCount} loans
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02] w-[4%]">#</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Loan ID</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Customer</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Lender</th>
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Settlement</th>
                    <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">Loan Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {settlementsSlice.map((loan, i) => (
                    <tr key={loan.loanId} className={i % 2 === 1 ? 'bg-white/[0.015]' : ''}>
                      <td className="px-4 py-2.5 text-gray-500">{startIdx + i + 1}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-emerald-400">{loan.loanId}</td>
                      <td className="px-4 py-2.5 text-gray-400">{loan.customerName}</td>
                      <td className="px-4 py-2.5 text-gray-400">{loan.lender}</td>
                      <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">{formatDate(loan.settlementDate)}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-medium text-white">{formatCurrency(loan.loanAmountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {newLoans.length > settlementsPageSize && (
                <Pagination
                  currentPage={settlementsPage}
                  totalItems={newLoans.length}
                  pageSize={settlementsPageSize}
                  onPageChange={setSettlementsPage}
                  onPageSizeChange={(size) => { setSettlementsPageSize(size); setSettlementsPage(1); }}
                />
              )}
            </div>
          </section>
        );
      })()}

      {/* Lender Concentration */}
      {lenderConcentration.length > 0 && (
        <section>
          <SectionHeading title="Lender Concentration" />
          <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl p-5 space-y-3">
            {lenderConcentration.slice(0, 6).map((lender, i) => (
              <div key={lender.lender} className="flex items-center gap-3">
                <div className="w-24 text-sm font-medium text-white text-right flex-shrink-0 truncate">{lender.lender}</div>
                <div className="flex-1 h-7 bg-white/[0.03] rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md flex items-center pl-2.5 text-[11px] font-mono font-medium text-white/90"
                    style={{
                      width: `${Math.max((lender.loanCount / maxLenderCount) * 100, 8)}%`,
                      background: `linear-gradient(90deg, ${lenderColors[i] || '#475569'}, ${lenderColors[i] || '#475569'}cc)`,
                    }}
                  >
                    {lender.loanCount}
                  </div>
                </div>
                <div className="w-28 text-right text-sm text-gray-400 flex-shrink-0">
                  {lender.percentage}% · {lender.loanCount} loans
                </div>
              </div>
            ))}
            {lenderConcentration.length > 6 && (
              <div className="flex items-center gap-3">
                <div className="w-24 text-sm font-medium text-white text-right flex-shrink-0">Other</div>
                <div className="flex-1 h-7 bg-white/[0.03] rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md flex items-center pl-2.5 text-[11px] font-mono font-medium text-white/90"
                    style={{
                      width: `${Math.max((lenderConcentration.slice(6).reduce((s, l) => s + l.loanCount, 0) / maxLenderCount) * 100, 8)}%`,
                      background: 'linear-gradient(90deg, #475569, #334155)',
                    }}
                  >
                    {lenderConcentration.slice(6).reduce((s, l) => s + l.loanCount, 0)}
                  </div>
                </div>
                <div className="w-28 text-right text-sm text-gray-400 flex-shrink-0">
                  {Math.round((lenderConcentration.slice(6).reduce((s, l) => s + l.loanCount, 0) / totalLenderLoans) * 100)}% · {lenderConcentration.slice(6).reduce((s, l) => s + l.loanCount, 0)} loans
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Portfolio Summary — only shown when broker has retained/acquired loans */}
      {data.retainedLoans.length > 0 && data.acquiredBookRetention && (
        <section>
          <SectionHeading title={`Acquired Book Retention — ${data.acquiredBookRetention.acquiredTemplateName}`} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Discharged" value={String(data.acquiredBookRetention.dischargedCount)} sub="From acquired book" color="#6b7280" />
            <MetricCard label="Retained" value={String(data.acquiredBookRetention.retainedCount)} sub="Refinanced back" color="#10b981" />
            <MetricCard label="Retained Value" value={formatCurrencyCompact(data.acquiredBookRetention.retainedValueCents)} sub="Total value retained" color="#f59e0b" />
            <MetricCard label="Retention Rate" value={`${data.acquiredBookRetention.retentionRate}%`} sub="Of discharged loans" color="#8b5cf6" />
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="text-center py-6 text-gray-500 text-xs border-t border-white/[0.06] space-y-1">
        <p>This is a broker performance report.</p>
        <p>Powered by <span className="text-emerald-500">VALUTTEN</span></p>
        <p className="text-gray-600">Data is based on commission records. Loan counts use unique Loan IDs.</p>
      </footer>

      {/* Share modal */}
      {shareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShareModalOpen(false)}>
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Share Report Link</h3>
            <p className="text-sm text-gray-400 mb-4">This link expires in 30 days. Anyone with the link can view this report.</p>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-300 truncate"
              />
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Sub-Components ---

const SectionHeading: React.FC<{ title: string; period?: string }> = ({ title, period }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="text-[13px] font-semibold uppercase tracking-[2px] text-gray-500">{title}</span>
    {period && (
      <span className="text-xs text-gray-400 bg-white/[0.04] rounded px-2 py-0.5">{period}</span>
    )}
    <div className="flex-1 h-px bg-white/[0.06]" />
  </div>
);

const MetricCard: React.FC<{ label: string; value: string; sub: string; color: string; hero?: boolean }> = ({ label, value, sub, color, hero }) => (
  <div className="relative bg-gray-800/50 border border-white/[0.06] rounded-xl px-5 py-5 overflow-hidden hover:bg-gray-800/70 transition-colors">
    <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl" style={{ background: color }} />
    <div className="absolute -top-7 -right-7 w-28 h-28 rounded-full blur-[50px] opacity-[0.12] pointer-events-none" style={{ background: color }} />
    <div className="text-[11px] font-medium uppercase tracking-[1.2px] text-gray-500 mb-2">{label}</div>
    <div className={`font-bold leading-none mb-1 ${hero ? 'text-[34px]' : 'text-[28px]'}`} style={{ color }}>{value}</div>
    <div className="text-sm text-gray-400">{sub}</div>
  </div>
);

const HealthCard: React.FC<{ value: number; label: string; desc: string; color: string }> = ({ value, label, desc, color }) => (
  <div className="relative bg-gray-800/50 border border-white/[0.06] rounded-xl px-5 py-5 overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl" style={{ background: color }} />
    <div className="text-[32px] font-bold leading-none mb-1" style={{ color }}>{value}</div>
    <div className="text-sm font-semibold text-white mb-1">{label}</div>
    <div className="text-xs text-gray-500 leading-snug">{desc}</div>
  </div>
);

export default BrokerReportPage;
